import React, { useEffect } from "react";
import {
  X,
  Clock,
  User,
  Mail,
  MessageCircle,
  AlertCircle,
  RefreshCw,
  Copy,
  Check,
} from "lucide-react";

import { useAppDispatch, useAppSelector } from "../../../hooks/useRedux";
import {
  clearPreview,
  previewMessage,
} from "../../../store/features/outbox/outboxActions";

const MessagePreviewModal = ({ messageId, isOpen, onClose }) => {
  const dispatch = useAppDispatch();
  const {
    previewMessage: message,
    isPreviewLoading,
    previewError,
  } = useAppSelector((state) => state.outbox);

  const [copied, setCopied] = React.useState(false);

  useEffect(() => {
    if (isOpen && messageId) {
      dispatch(previewMessage(messageId));
    }
  }, [isOpen, messageId, dispatch]);

  useEffect(() => {
    if (!isOpen) {
      dispatch(clearPreview());
    }
  }, [isOpen, dispatch]);

  const getStatusBadge = (status) => {
    const statusConfig = {
      queued: {
        class: "bg-warning text-dark border-0",
        icon: <Clock size={14} className="me-1" />,
        pulse: true,
      },
      processing: {
        class: "bg-primary text-white border-0",
        icon: <RefreshCw size={14} className="me-1 animate-spin" />,
        pulse: true,
      },
      sent: {
        class: "bg-success text-white border-0",
        icon: <Check size={14} className="me-1" />,
        pulse: false,
      },
      failed: {
        class: "bg-danger text-white border-0",
        icon: <AlertCircle size={14} className="me-1" />,
        pulse: false,
      },
    };

    const config = statusConfig[status];
    return (
      <span
        className={`badge ${config.class} ${
          config.pulse ? "badge-pulse" : ""
        } d-flex align-items-center`}
      >
        {config.icon}
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </span>
    );
  };

  const getMediaIcon = (media) => {
    const mediaConfig = {
      sms: {
        icon: <MessageCircle size={18} />,
        color: "text-info",
        bg: "bg-info",
      },
      email: {
        icon: <Mail size={18} />,
        color: "text-primary",
        bg: "bg-primary",
      },
      whatsapp: {
        icon: <MessageCircle size={18} />,
        color: "text-success",
        bg: "bg-success",
      },
      telegram: {
        icon: <MessageCircle size={18} />,
        color: "text-info",
        bg: "bg-info",
      },
    };

    const config = mediaConfig[media] || {
      icon: <MessageCircle size={18} />,
      color: "text-secondary",
      bg: "bg-secondary",
    };

    return (
      <div className="d-flex align-items-center">
        <div className={`rounded-circle ${config.bg} bg-opacity-10 p-2 me-2`}>
          <span className={config.color}>{config.icon}</span>
        </div>
        <span className="text-capitalize fw-medium">{media}</span>
      </div>
    );
  };

  const formatTimestamp = (timestamp) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diff = now - date;
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));

    if (days === 0) {
      return `Today at ${date.toLocaleTimeString([], {
        hour: "2-digit",
        minute: "2-digit",
      })}`;
    } else if (days === 1) {
      return `Yesterday at ${date.toLocaleTimeString([], {
        hour: "2-digit",
        minute: "2-digit",
      })}`;
    } else if (days < 7) {
      return `${days} days ago`;
    } else {
      return date.toLocaleDateString();
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  if (!isOpen) return null;

  return (
    <>
      <div
        className="modal fade show d-block modal-backdrop-custom"
        tabIndex="-1"
        onClick={(e) => e.target === e.currentTarget && onClose()}
      >
        <div className="modal-dialog modal-lg modal-dialog-centered">
          <div className="modal-content modal-content-custom">
            <div className="modal-header modal-header-custom">
              <div>
                <h4 className="modal-title fw-bold mb-1">Message Preview</h4>
                <p className="text-muted mb-0 small">
                  View detailed message information
                </p>
              </div>
              <button
                type="button"
                className="btn-close"
                onClick={onClose}
                aria-label="Close"
              ></button>
            </div>

            <div className="modal-body modal-body-custom">
              {isPreviewLoading && (
                <div className="text-center py-5">
                  <div
                    className="spinner-border text-primary mb-3"
                    role="status"
                    style={{ width: "3rem", height: "3rem" }}
                  >
                    <span className="visually-hidden">Loading...</span>
                  </div>
                  <h6 className="text-muted">Loading message details...</h6>
                  <p className="text-muted small">
                    Please wait while we fetch the information
                  </p>
                </div>
              )}

              {previewError && (
                <div className="alert alert-danger d-flex align-items-center border-0 rounded-3">
                  <AlertCircle size={20} className="me-2 flex-shrink-0" />
                  <div>
                    <strong>Error loading message</strong>
                    <div className="small mt-1">{previewError.message}</div>
                  </div>
                </div>
              )}

              {message && !isPreviewLoading && (
                <div className="row g-4">
                  <div className="col-lg-8">
                    <div className="card card-custom">
                      <div className="card-header card-header-custom">
                        <div className="d-flex align-items-center justify-content-between">
                          <h6 className="mb-0 fw-bold">
                            <MessageCircle size={18} className="me-2" />
                            Message Content
                          </h6>
                          <small className="text-muted ms-1">
                            ({message.message?.length || 0} characters)
                          </small>
                        </div>
                      </div>
                      <div className="card-body">
                        <div className="message-content">
                          <button
                            className="copy-button"
                            onClick={() => copyToClipboard(message.message)}
                            title="Copy message"
                          >
                            {copied ? <Check size={12} /> : <Copy size={12} />}
                          </button>
                          <p
                            className="mb-0"
                            style={{ whiteSpace: "pre-wrap" }}
                          >
                            {message.message}
                          </p>
                        </div>
                      </div>
                    </div>

                    {message.error && (
                      <div className="card card-custom error-card mt-3">
                        <div className="card-header bg-danger text-white">
                          <h6 className="mb-0 fw-bold">
                            <AlertCircle size={18} className="me-2" />
                            Error Details
                          </h6>
                        </div>
                        <div className="card-body">
                          <div className="d-flex align-items-start">
                            <AlertCircle
                              size={20}
                              className="me-2 text-danger flex-shrink-0 mt-1"
                            />
                            <div>
                              <div className="fw-medium text-danger">
                                Delivery Failed
                              </div>
                              <div className="small text-muted mt-1">
                                {message.error}
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>

                  <div className="col-lg-4">
                    <div className="card card-custom">
                      <div className="card-header card-header-custom">
                        <h6 className="mb-0 fw-bold">
                          <User size={18} className="me-2" />
                          Message Details
                        </h6>
                      </div>
                      <div className="card-body">
                        <div className="detail-item">
                          <div className="detail-label">Recipient</div>
                          <div className="d-flex align-items-center">
                            <div>
                              <div className="fw-medium">
                                {message.recipient}
                              </div>
                              <small className="text-muted">Destination</small>
                            </div>
                          </div>
                        </div>

                        <div className="detail-item">
                          <div className="detail-label">Media Channel</div>
                          {getMediaIcon(message.media)}
                        </div>

                        <div className="detail-item">
                          <div className="detail-label">Current Status</div>
                          {getStatusBadge(message.status)}
                        </div>

                        <div className="detail-item">
                          <div className="detail-label">Created</div>
                          <div className="d-flex align-items-center">
                            <Clock size={16} className="me-2 text-muted" />
                            <div>
                              <small className="text-muted">
                                {new Date(message.created_at).toLocaleString()}
                              </small>
                            </div>
                          </div>
                        </div>

                        {message.sent_at && (
                          <div className="detail-item">
                            <div className="detail-label">Delivered</div>
                            <div className="d-flex align-items-center">
                              <Check size={16} className="me-2 text-success" />
                              <div>
                                <div className="fw-medium small">
                                  {formatTimestamp(message.sent_at)}
                                </div>
                                <small className="text-muted">
                                  {new Date(message.sent_at).toLocaleString()}
                                </small>
                              </div>
                            </div>
                          </div>
                        )}

                        {message.retryCount > 0 && (
                          <div className="detail-item">
                            <div className="detail-label">Retry Attempts</div>
                            <div className="d-flex align-items-center">
                              <RefreshCw
                                size={16}
                                className="me-2 text-warning"
                              />
                              <span className="badge bg-warning text-dark fw-medium">
                                {message.retryCount} attempts
                              </span>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>

            <div className="modal-footer modal-footer-custom">
              <button
                type="button"
                className="btn btn-light border"
                onClick={onClose}
              >
                Close
              </button>
              {/* {message && message.status === "failed" && (
                <button
                  type="button"
                  className="btn btn-retry text-white"
                  onClick={() => {
                    console.log("Retry message:", message.id);
                  }}
                >
                  <RefreshCw size={16} className="me-2" />
                  Retry Message
                </button>
              )} */}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default MessagePreviewModal;
